import { Badge } from '@/components/ui/Badge';
import type { TradingEnvironment } from '@/api/trading';

export type DeploymentAction = 'start' | 'restart' | 'stop' | 'flatten' | 'force-stop' | 'retire' | 'force-retire';

const routineDeploymentActions = new Set<DeploymentAction>(['start', 'restart', 'stop', 'flatten']);

export function EnvironmentBadge({ environment }: { environment?: TradingEnvironment | null }) {
  const label = environment === 'Real' ? 'Live · real money' : environment ?? 'Unknown';
  const variant = environment === 'Real' ? 'danger' : environment === 'Demo' ? 'warning' : 'info';
  return <Badge variant={variant}>{label}</Badge>;
}

export function LifecycleBadge({ status }: { status: string }) {
  const variant = status === 'Active' ? 'success' : ['Faulted', 'ActivationFailed'].includes(status) ? 'danger'
    : ['Offline', 'RecoveryRequired', 'StoppedWithExposure', 'ForceStopped', 'RiskPaused'].includes(status) ? 'warning' : 'info';
  return <Badge variant={variant}>{status}</Badge>;
}

export function Metric({ label, value, detail }: { label: string; value: string | number; detail?: string }) {
  return <div><span>{label}</span><strong>{value}</strong>{detail && <small>{detail}</small>}</div>;
}

export function availableActions(status: string): DeploymentAction[] {
  const result: DeploymentAction[] = [];
  if (status === 'Created') result.push('start', 'retire');
  if (['RecoveryRequired', 'Stopped', 'StoppedWithExposure', 'ActivationFailed', 'ForceStopped', 'Faulted', 'RiskPaused'].includes(status)) result.push('restart');
  if (['Starting', 'Reconciling', 'WarmingUp', 'Active', 'Offline', 'RiskPaused', 'StopPending', 'RecoveryRequired', 'Faulted'].includes(status)) result.push('stop');
  if (['Active', 'Offline', 'RiskPaused', 'StoppedWithExposure', 'StopPending', 'RecoveryRequired', 'ForceStopped', 'Faulted'].includes(status)) result.push('flatten');
  if (['Starting', 'Reconciling', 'WarmingUp', 'Active', 'Offline', 'RiskPaused', 'StopPending', 'RecoveryRequired', 'Faulted'].includes(status)) result.push('force-stop');
  if (['Stopped', 'ActivationFailed'].includes(status)) result.push('retire');
  if (['Faulted', 'RecoveryRequired', 'StopPending', 'StoppedWithExposure', 'ForceStopped'].includes(status)) result.push('force-retire');
  return result;
}

export function availableActionsForRole(status: string, role?: string): DeploymentAction[] {
  const actions = availableActions(status);
  if (role === 'Admin') return actions;
  if (role === 'Trader') return actions.filter(action => routineDeploymentActions.has(action));
  return [];
}

export function actionLabel(action: DeploymentAction) {
  return action === 'flatten' ? 'Cleanup & stop' : action === 'force-stop' ? 'Force stop'
    : action === 'force-retire' ? 'Force release & retire' : action === 'retire' ? 'Retire' : action;
}

export function formatTime(value?: string | null) {
  return value ? new Date(value).toLocaleString() : '—';
}

export function formatPrice(value: number, tickSize?: number) {
  const tick = tickSize && tickSize > 0 ? tickSize.toFixed(10).replace(/0+$/, '') : '';
  const digits = tick.includes('.') ? tick.length - tick.indexOf('.') - 1 : tick ? 0 : 6;
  return value.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function formatMoney(value?: number | null, currency?: string | null) {
  return value == null ? '—' : `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}${currency ? ` ${currency}` : ''}`;
}
